import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Play } from 'lucide-react';
import RevolvingBadge from './RevolvingBadge';

// Auto-rotating status text — swaps every 2.8s with a slide+fade
const STATUS_MESSAGES = [
  'Taking 2 new brands · Feb 2026',
  '48-hour reply · UK time',
  'Currently on 3 active projects',
  'Rutvi Ahir · Founder + Creator',
];

const RotatingStatus = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % STATUS_MESSAGES.length), 2800);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="relative inline-flex overflow-hidden h-4 min-w-[220px] sm:min-w-[260px]">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={i}
          initial={{ y: '110%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '-110%', opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.2, 0.9, 0.25, 1] }}
          className="absolute inset-0 text-[11px] font-semibold uppercase tracking-[0.18em] whitespace-nowrap"
        >
          {STATUS_MESSAGES[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

// Character-level title reveal with stagger + spring
const CharTitle = ({ text, className = '', style = {}, delay = 0.15 }) => {
  const words = text.split(' ');
  return (
    <h1 className={className} style={style} aria-label={text}>
      {words.map((w, wi) => (
        <span key={wi} className="inline-block mr-[0.22em] whitespace-nowrap">
          {w.split('').map((ch, ci) => (
            <span
              key={ci}
              className="inline-block overflow-hidden align-baseline"
              style={{ paddingBottom: '0.14em' }}
            >
              <motion.span
                className="inline-block"
                initial={{ y: '110%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  duration: 0.75,
                  ease: [0.2, 0.9, 0.25, 1],
                  delay: delay + (wi * 4 + ci) * 0.025,
                }}
              >
                {ch}
              </motion.span>
            </span>
          ))}
        </span>
      ))}
    </h1>
  );
};

// Small floating star SVG decoration
const Star = ({ className, delay = 0, color = '#AA94FF' }) => (
  <motion.svg
    className={className}
    viewBox="0 0 24 24"
    initial={{ scale: 0, rotate: 0, opacity: 0 }}
    animate={{ scale: 1, rotate: 360, opacity: 1 }}
    transition={{
      scale: { duration: 0.6, delay },
      opacity: { duration: 0.6, delay },
      rotate: { duration: 20, repeat: Infinity, ease: 'linear' },
    }}
    aria-hidden="true"
  >
    <path
      d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z"
      fill={color}
    />
  </motion.svg>
);

// Tilted media card that tracks pointer
const TiltCard = ({ children, className = '', tilt = 6, delay = 0 }) => {
  const ref = useRef(null);
  const rotX = useSpring(0, { damping: 20, stiffness: 200 });
  const rotY = useSpring(0, { damping: 20, stiffness: 200 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      rotY.set(px * tilt * 2);
      rotX.set(-py * tilt * 2);
    };
    const onLeave = () => {
      rotX.set(0);
      rotY.set(0);
    };
    window.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [tilt, rotX, rotY]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.8, delay, ease: [0.2, 0.9, 0.25, 1] }}
      style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 1000 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Hero = () => {
  const scrollToContact = () =>
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });

  // Parallax for background blobs
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const blob1X = useSpring(useTransform(mx, [-1, 1], [-30, 30]), { damping: 22 });
  const blob1Y = useSpring(useTransform(my, [-1, 1], [-30, 30]), { damping: 22 });
  const blob2X = useSpring(useTransform(mx, [-1, 1], [30, -30]), { damping: 22 });
  const blob2Y = useSpring(useTransform(my, [-1, 1], [30, -30]), { damping: 22 });

  useEffect(() => {
    const onMove = (e) => {
      mx.set((e.clientX / window.innerWidth) * 2 - 1);
      my.set((e.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [mx, my]);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 pb-16 sm:pt-36 sm:pb-24 px-6 sm:px-8 lg:px-12 bg-[#F4F4F3] overflow-hidden"
    >
      {/* AURORA MESH — three animated blobs with pointer parallax */}
      <motion.div
        aria-hidden="true"
        style={{ x: blob1X, y: blob1Y }}
        className="pointer-events-none absolute top-[-15%] right-[-10%] w-[720px] h-[720px] rounded-full bg-[#AA94FF] opacity-40 blur-3xl mix-blend-multiply"
      />
      <motion.div
        aria-hidden="true"
        style={{ x: blob2X, y: blob2Y }}
        className="pointer-events-none absolute top-[10%] left-[-15%] w-[600px] h-[600px] rounded-full bg-[#FFA8F2] opacity-40 blur-3xl mix-blend-multiply"
      />
      <motion.div
        aria-hidden="true"
        animate={{ scale: [1, 1.1, 1], rotate: [0, 30, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        className="pointer-events-none absolute bottom-[-10%] left-[30%] w-[520px] h-[520px] rounded-full bg-[#FFA952] opacity-30 blur-3xl mix-blend-multiply"
      />

      {/* Subtle noise/grain overlay for depth */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence baseFrequency=%220.8%22 numOctaves=%223%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")',
        }}
      />

      {/* Floating stars scattered */}
      <Star className="absolute top-[18%] right-[8%] w-8 h-8" delay={1.2} color="#AA94FF" />
      <Star className="absolute top-[45%] right-[42%] w-5 h-5" delay={1.5} color="#FFA8F2" />
      <Star className="absolute top-[65%] left-[6%] w-6 h-6" delay={1.8} color="#FFA952" />

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* LEFT — Text block */}
        <div className="lg:col-span-7 relative z-10">
          {/* Live rotating status pill — cycles through multiple messages */}
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.55 }}
            className="relative inline-flex items-center gap-2.5 mb-8 rounded-full p-[2px] overflow-hidden"
          >
            {/* Animated conic-gradient border */}
            <motion.span
              aria-hidden="true"
              animate={{ rotate: 360 }}
              transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
              className="absolute inset-[-100%]"
              style={{
                background:
                  'conic-gradient(from 0deg, #AA94FF, #FFA8F2, #FFA952, #AA94FF)',
              }}
            />
            {/* Inner pill */}
            <span className="relative flex items-center gap-2.5 bg-[#1C1917] text-[#F4F4F3] rounded-full pl-2 pr-4 py-1.5 z-10">
              <span className="relative flex w-2.5 h-2.5">
                <motion.span
                  animate={{ scale: [1, 2.5, 1], opacity: [0.6, 0, 0.6] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                  className="absolute inline-flex h-full w-full rounded-full bg-[#AA94FF]"
                />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#AA94FF]" />
              </span>
              <RotatingStatus />
            </span>
          </motion.div>

          {/* Editorial mixed-typography headline */}
          <div style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} className="mb-4">
            {/* Row 1: "Content" huge + "that" small tracked */}
            <div className="flex items-end gap-4 sm:gap-6 flex-wrap">
              <CharTitle
                text="Content"
                className="text-6xl sm:text-8xl lg:text-[9rem] font-black tracking-[-0.045em] leading-[0.85] text-[#1C1917]"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                delay={0.15}
              />
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="pb-3 sm:pb-5 text-xs sm:text-sm font-bold uppercase tracking-[0.35em] text-[#78716C]"
              >
                / that
              </motion.span>
            </div>

            {/* Row 2: "actually" italic + hand-drawn SVG underline */}
            <div className="relative inline-block my-2 sm:my-3">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.75 }}
                className="text-6xl sm:text-8xl lg:text-[9rem] italic font-light tracking-[-0.02em] leading-[0.85] text-[#AA94FF]"
                style={{ fontFamily: "'Cabinet Grotesk', serif" }}
              >
                actually
              </motion.div>
              {/* Hand-drawn underline that draws in */}
              <motion.svg
                aria-hidden="true"
                viewBox="0 0 400 30"
                preserveAspectRatio="none"
                className="absolute left-0 -bottom-1 sm:-bottom-2 w-full h-4 sm:h-6"
              >
                <motion.path
                  d="M 4 20 Q 100 5, 200 15 T 396 12"
                  fill="none"
                  stroke="#FFA8F2"
                  strokeWidth="6"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 1.15, ease: 'easeInOut' }}
                />
              </motion.svg>
              {/* Tilted small tag next to word */}
              <motion.span
                initial={{ opacity: 0, scale: 0, rotate: 0 }}
                animate={{ opacity: 1, scale: 1, rotate: 12 }}
                transition={{ duration: 0.5, delay: 1.3, type: 'spring', damping: 12 }}
                className="hidden sm:inline-block absolute -right-16 top-4 bg-[#1C1917] text-[#F4F4F3] text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full"
              >
                ← key
              </motion.span>
            </div>

            {/* Row 3: "grows" huge + "your" italic small */}
            <div className="flex items-baseline gap-3 sm:gap-6 flex-wrap mt-1">
              <CharTitle
                text="grows"
                className="text-6xl sm:text-8xl lg:text-[9rem] font-black tracking-[-0.045em] leading-[0.85] text-[#1C1917]"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                delay={1.15}
              />
              <motion.span
                initial={{ opacity: 0, y: 20, rotate: -6 }}
                animate={{ opacity: 1, y: 0, rotate: -3 }}
                transition={{ duration: 0.6, delay: 1.45 }}
                className="text-3xl sm:text-5xl lg:text-6xl italic font-light text-[#FFA952]"
                style={{ fontFamily: "'Cabinet Grotesk', serif" }}
              >
                your
              </motion.span>
            </div>

            {/* Row 4: "brand." — massive with animated highlight sweep */}
            <div className="relative inline-block">
              {/* Highlight sweep behind */}
              <motion.span
                aria-hidden="true"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 1.75, ease: [0.2, 0.9, 0.25, 1] }}
                className="absolute inset-x-0 top-[15%] bottom-[15%] bg-[#AA94FF]/25 origin-left rounded-lg"
              />
              <CharTitle
                text="brand."
                className="relative text-6xl sm:text-8xl lg:text-[9rem] font-black tracking-[-0.045em] leading-[0.85] text-[#1C1917]"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                delay={1.5}
              />
            </div>
          </div>

          {/* Subtitle + CTAs */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="mt-12 max-w-lg text-base sm:text-lg leading-relaxed text-[#78716C]"
          >
            HypeLab helps brands show up on social with UGC, short-form video and a strategy
            built for scroll-stopping — not guesswork. Creator-led, results-obsessed.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.25 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <div className="text-[#1C1917]">
              <RevolvingBadge
                text="BOOK A CALL · HYPELAB · "
                size={140}
                onClick={scrollToContact}
              />
            </div>
          </motion.div>

          {/* Trust row: mini stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-md border-t border-[#1C1917]/10 pt-6"
          >
            {[
              { v: '1M+', l: 'Impressions' },
              { v: '120%', l: 'Avg growth' },
              { v: '10+', l: 'Brands' },
            ].map((s, i) => (
              <div key={i}>
                <p
                  className="text-2xl sm:text-3xl font-black text-[#1C1917] tracking-[-0.03em]"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {s.v}
                </p>
                <p className="text-[11px] uppercase tracking-[0.15em] text-[#78716C] font-semibold mt-1">
                  {s.l}
                </p>
              </div>
            ))}
          </motion.div>
        </div>

        {/* RIGHT — 3D tilted media collage */}
        <div className="lg:col-span-5 relative z-10">
          <div className="relative h-[520px] sm:h-[600px] lg:h-[640px] max-w-md mx-auto">
            {/* Main tall card */}
            <TiltCard
              tilt={5}
              delay={0.9}
              className="absolute top-0 right-0 w-[70%] h-[80%] rounded-[2rem] overflow-hidden shadow-2xl bg-[#1C1917] rotate-[3deg] hover:rotate-0 transition-transform duration-500"
            >
              <img
                src="https://images.unsplash.com/photo-1611605698335-8b1569810432?w=800&auto=format&fit=crop&q=75"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/70 to-transparent" />
              <div className="absolute bottom-4 left-4 flex items-center gap-2 text-[#F4F4F3]">
                <div className="w-10 h-10 rounded-full bg-[#AA94FF] flex items-center justify-center">
                  <Play className="w-4 h-4 text-[#1C1917] ml-0.5 fill-current" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider">Reel</p>
                  <p className="text-[10px] opacity-70">forrealrutviii</p>
                </div>
              </div>
              <div className="absolute top-4 right-4 bg-[#F4F4F3]/95 text-[#1C1917] px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest">
                LIVE
              </div>
            </TiltCard>

            {/* Bottom-left small card */}
            <TiltCard
              tilt={8}
              delay={1.1}
              className="absolute bottom-0 left-0 w-[55%] h-[45%] rounded-[1.5rem] overflow-hidden shadow-2xl bg-[#FFA8F2] rotate-[-6deg] hover:rotate-0 transition-transform duration-500"
            >
              <img
                src="https://images.unsplash.com/photo-1533062618053-d51e617307ec?w=600&auto=format&fit=crop&q=75"
                alt=""
                className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity"
              />
              <div className="absolute inset-0 bg-[#AA94FF]/30 mix-blend-color" />
            </TiltCard>

            {/* Top-left stats bubble */}
            <TiltCard
              tilt={4}
              delay={1.25}
              className="absolute top-[8%] left-[-4%] w-[46%] bg-[#F4F4F3] border border-[#1C1917]/10 rounded-2xl p-4 shadow-xl -rotate-[4deg] hover:rotate-0 transition-transform duration-500"
            >
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#78716C] font-bold mb-1">
                Views this week
              </p>
              <p
                className="text-3xl font-black text-[#1C1917] tracking-[-0.03em]"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                284K
              </p>
              <div className="mt-2 flex items-center gap-1 text-[10px] text-[#AA94FF] font-bold">
                <svg viewBox="0 0 12 12" width="12" height="12" fill="currentColor">
                  <path d="M6 2 L11 8 L1 8 Z" />
                </svg>
                +18%
              </div>
            </TiltCard>

            {/* Bottom-right badge */}
            <TiltCard
              tilt={4}
              delay={1.4}
              className="absolute bottom-[8%] right-[-2%] bg-[#AA94FF] text-[#1C1917] rounded-2xl px-4 py-3 shadow-xl rotate-[5deg] hover:rotate-0 transition-transform duration-500"
            >
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold opacity-70">Now</p>
              <p
                className="text-lg font-black tracking-tight"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                Booking Q2
              </p>
            </TiltCard>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
